/**
 * LeadsPage.tsx — the Leads list cockpit (story E0-BO-LS7).
 *
 * Consumes `POST /api/leads/search` through the injected API client and renders
 * the newest-first list with filters, one search box (ADR-089), and KEYSET
 * Previous/Next pagination (25 per page) over the envelope's opaque
 * `next_cursor` (null = last page). Default load sends an empty status list, so
 * the BFF returns the active/needs-attention scope (Q2); the status dropdown
 * widens to terminal states.
 *
 * First-class states (spec §Error Handling): loading, empty, and a clean error
 * for the BFF's 502 "source unavailable" — an operator-readable message + retry,
 * never a crash or a blank page. A 401/403 renders the shared UnauthorizedScreen.
 *
 * Each row exposes a link to `#leads/<id>` — the navigation SEAM for the LS8
 * detail view; this story does not render detail.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient, LeadsQuery } from '../../api/client';
import type { LeadListItem } from '../../api/types';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { loadPersistedFilters, savePersistedFilters } from '../../lib/persistFilters';
import { LeadStatusBadge } from './LeadStatusBadge';
import { LeadsFilters } from './LeadsFilters';
import { NewLeadForm } from './NewLeadForm';
import { formatDateTime } from './formatDateTime';
import { LEADS_PAGE_SIZE, useLeadsQuery } from './useLeadsQuery';
import { Button } from '../../components/Button';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/Skeleton';
import floating from '../../lib/floating';

export interface LeadsPageProps {
  apiClient: ApiClient;
}

const DEFAULT_QUERY: LeadsQuery = { status: [], limit: LEADS_PAGE_SIZE };
/** sessionStorage key for the last-applied Leads filter set (search stripped). */
const LEADS_FILTERS_KEY = 'tcn.backoffice.leads.filters.v1';

// Persist coarse selections only — NEVER the search text (personal data) or a cursor.
const persistableQuery = (query: LeadsQuery): LeadsQuery => {
  const rest = { ...query };
  delete rest.q;
  delete rest.cursor;
  return rest;
};

// A remembered set may predate the fixed page size (or hold a legacy `search`/`q`) —
// force the size and drop anything that could carry personal data.
const initialQuery = (): LeadsQuery => ({
  ...persistableQuery(loadPersistedFilters<LeadsQuery>(LEADS_FILTERS_KEY) ?? DEFAULT_QUERY),
  limit: LEADS_PAGE_SIZE,
});
const pagerButtonClass =
  'inline-flex items-center rounded-lg bg-surface-2 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-1 disabled:opacity-60 max-md:min-h-11';

const LeadsTable = ({ items, locale }: { items: LeadListItem[]; locale: string }) => {
  const { t } = useTranslation();
  const dash = <span className="text-ink-2">—</span>;
  const columns: DataTableColumn<LeadListItem>[] = [
    {
      key: 'name',
      header: t('leads.columns.name'),
      link: true,
      renderCell: (lead) => (
        <a
          href={`#leads/${lead.id}`}
          data-testid="lead-link"
          aria-label={t('leads.viewLead', { name: lead.display_name ?? lead.id })}
          className="rounded text-gold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus"
        >
          {lead.display_name ?? '—'}
        </a>
      ),
    },
    {
      key: 'status',
      header: t('leads.columns.status'),
      renderCell: (lead) => <LeadStatusBadge status={lead.status} />,
    },
    {
      key: 'statusUpdated',
      header: t('leads.columns.statusUpdated'),
      renderCell: (lead) => (
        <span className="text-ink-2">{formatDateTime(lead.status_updated_at, locale)}</span>
      ),
    },
    // Masked at capital-service (ADR-089): rendered verbatim, never re-formatted.
    {
      key: 'cpf',
      header: t('leads.columns.cpf'),
      renderCell: (lead) => (lead.cpf_masked != null ? <span>{lead.cpf_masked}</span> : dash),
    },
    {
      key: 'phone',
      header: t('leads.columns.phone'),
      renderCell: (lead) => (lead.phone_masked != null ? <span>{lead.phone_masked}</span> : dash),
    },
    {
      key: 'product',
      header: t('leads.columns.product'),
      renderCell: (lead) => <span className="text-ink-2">{lead.product_code ?? '—'}</span>,
    },
    {
      key: 'channel',
      header: t('leads.columns.channel'),
      renderCell: (lead) => <span className="text-ink-2">{lead.channel}</span>,
    },
    {
      key: 'created',
      header: t('leads.columns.created'),
      renderCell: (lead) => (
        <span className="text-ink-2">{formatDateTime(lead.created_at, locale)}</span>
      ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={items}
      rowKey={(lead) => lead.id}
      caption={t('leads.tableLabel')}
      data-testid="leads-table"
      rowTestId="lead-row"
    />
  );
};

export const LeadsPage = ({ apiClient }: LeadsPageProps) => {
  const { t, i18n } = useTranslation();
  const { items, nextCursor, total, phase, page, errored, searchError, load, goNext, goPrev } =
    useLeadsQuery(apiClient);

  // The last applied filter set — seeded from the remembered set so a returning
  // operator lands on the view they left. Re-run on Retry without re-deriving it.
  const [appliedQuery, setAppliedQuery] = useState<LeadsQuery>(initialQuery);
  const didMount = useRef(false);
  // LS12: the "New lead" form is mounted inline, toggled by a CTA — no route
  // change (the existing `#leads/<id>` route already covers the post-create
  // navigation to the new record's detail view).
  const [showNewLeadForm, setShowNewLeadForm] = useState(false);

  // Initial load: the remembered filter set, else the default active scope (Q2).
  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;
    floating(load(appliedQuery));
  }, [load, appliedQuery]);

  const handleApply = useCallback(
    (query: LeadsQuery) => {
      setAppliedQuery(query);
      savePersistedFilters(LEADS_FILTERS_KEY, persistableQuery(query));
      floating(load(query));
    },
    [load],
  );

  const handleRetry = useCallback(() => {
    floating(load(appliedQuery));
  }, [appliedQuery, load]);

  // Global "refresh" hotkey (see AppShell) re-runs the current query.
  useEffect(() => {
    const onRefresh = () => floating(load(appliedQuery));
    window.addEventListener('tcn:refresh', onRefresh);
    return () => window.removeEventListener('tcn:refresh', onRefresh);
  }, [load, appliedQuery]);

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  const busy = phase === 'loading';
  const pageLabel = t('leads.pagination.range', {
    from: ((page - 1) * LEADS_PAGE_SIZE + 1).toLocaleString(i18n.language),
    to: ((page - 1) * LEADS_PAGE_SIZE + items.length).toLocaleString(i18n.language),
    total: total.toLocaleString(i18n.language),
  });

  const renderBranch1 = () => {
    if (phase === 'loading' && items.length === 0)
      return <Skeleton data-testid="leads-loading" label={t('leads.loading')} />;
    if (phase === 'error' && items.length === 0)
      return (
        <Notice
          tone="danger"
          data-testid="leads-error"
          data-focal="true"
          action={
            <Button data-testid="leads-retry" onClick={handleRetry}>
              {t('leads.retry')}
            </Button>
          }
        >
          {t('leads.errorBody')}
        </Notice>
      );
    if (items.length === 0)
      return <EmptyState data-testid="leads-empty" title={t('leads.empty')} />;
    return <LeadsTable items={items} locale={i18n.language} />;
  };

  return (
    <section aria-labelledby="leads-heading" className="flex flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 id="leads-heading" className="text-h1 text-ink-1">
          {t('leads.title')}
        </h1>
        <div className="flex items-center gap-3">
          {!showNewLeadForm ? (
            <button
              type="button"
              data-testid="leads-new-lead-cta"
              onClick={() => setShowNewLeadForm(true)}
              className="inline-flex min-h-11 items-center rounded-lg bg-gold px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold/90"
            >
              {t('leads.newLead.cta')}
            </button>
          ) : null}
        </div>
      </header>

      {showNewLeadForm ? (
        <NewLeadForm
          apiClient={apiClient}
          onCancel={() => setShowNewLeadForm(false)}
          onCreated={(leadId) => {
            setShowNewLeadForm(false);
            window.location.hash = `leads/${encodeURIComponent(leadId)}`;
          }}
        />
      ) : null}

      <LeadsFilters
        onApply={handleApply}
        busy={busy}
        applied={appliedQuery}
        locale={i18n.language}
        searchError={searchError}
      />

      {/* SINGLE error surface. With rows on screen (a refresh failed but the
          last-known list survives) it's a compact banner. With NO rows the same
          failure becomes the focal (elev-2 status-down ring) — the loudest thing
          on the panel — never both at once (dedup). */}
      {errored && items.length > 0 ? (
        <p
          role="alert"
          data-testid="leads-error-banner"
          className="rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40"
        >
          {t('leads.errorBody')}
        </p>
      ) : null}

      {renderBranch1()}

      {items.length > 0 ? (
        <nav
          aria-label={t('leads.pagination.nav')}
          data-testid="leads-pagination"
          className="flex items-center gap-3"
        >
          <button
            type="button"
            data-testid="leads-prev"
            onClick={() => floating(goPrev())}
            disabled={page === 1 || busy}
            className={pagerButtonClass}
          >
            {t('leads.pagination.previous')}
          </button>
          <p role="status" data-testid="leads-page-label" className="text-meta text-ink-3">
            {pageLabel}
          </p>
          <button
            type="button"
            data-testid="leads-next"
            onClick={() => floating(goNext())}
            disabled={nextCursor == null || busy}
            className={pagerButtonClass}
          >
            {t('leads.pagination.next')}
          </button>
        </nav>
      ) : null}
    </section>
  );
};
